import React, { PureComponent } from 'react';
import { find, isNil, size, get, cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import apolloClient from '@src/graphql/apollo';
import SectionTitle from '@src/components/SectionTitle';
import { Tabs } from '@src/components/Tabs';
import { TextField } from '@src/components/TextField';
import { DEFAULT_CURRENCY_ID } from '@src/constants';
import { trackSetSalaryRange } from '@src/metrics';
import { AvailableCurrenciesQueryDocument, AvailableCurrenciesQueryQuery } from '@src/graphql/generated';
import { SalariesChart } from './SalariesChart';
import { GrossSalaryNotice } from './GrossSalaryNotice';
import './styles.scss';

const PERIOD_TABS = [
  { key: 'HOUR', label: 'Hourly' },
  { key: 'MONTH', label: 'Monthly' },
  { key: 'YEAR', label: 'Yearly' },
];

function getDefaultSalaryRange(employmentType, activeSalaryRange) {
  if (activeSalaryRange && activeSalaryRange.period) {
    return activeSalaryRange;
  }

  return {
    currency: DEFAULT_CURRENCY_ID,
    from: null,
    period: employmentType === 'PART_TIME' ? 'HOUR' : 'MONTH',
    to: null,
  };
}

function setActivePeriod(employmentType, activeSalary) {
  if (activeSalary && activeSalary.period) {
    return find(PERIOD_TABS, { key: activeSalary.period });
  }

  return employmentType === 'PART_TIME' ? PERIOD_TABS[0] : PERIOD_TABS[1];
}

function validateFieldsBeforeSave(salary) {
  if (salary.currency && salary.period && salary.from && salary.to && salary.to >= salary.from) {
    return true;
  }

  return false;
}

function getOpeningSalaryData(dataParams) {
  const data = cloneDeep(dataParams);

  if (data && data.__typename) {
    delete data.__typename;
  }

  if (data && data.currency && data.currency.__typename) {
    delete data.currency.__typename;
  }

  const additionalFields = {};

  return {
    ...data,
    ...additionalFields,
    currency: get(data, 'currency.id', DEFAULT_CURRENCY_ID),
  };
}

export default class SalaryBudget extends PureComponent {
  static propTypes = {
    activeSalary: PropTypes.object,
    employmentType: PropTypes.string,
    openingId: PropTypes.string,
    updatedAt: PropTypes.string,
  };

  _isMounted = false;

  state = {
    activePeriodTab: setActivePeriod(this.props.employmentType, this.props.activeSalary),
    currentSalary: getDefaultSalaryRange(this.props.employmentType, this.props.activeSalary),
    maxInvalid: false,
    salaryCurrencies: [],
  };

  async componentDidMount() {
    this._isMounted = true;

    const {
      data: { availableCurrencies },
    } = await apolloClient.query<AvailableCurrenciesQueryQuery>({
      query: AvailableCurrenciesQueryDocument,
    });

    if (this._isMounted) {
      this.setState({
        salaryCurrencies: availableCurrencies,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setSalaryBudget() {
    const { employmentType, activeSalary } = this.props;

    const salary = {
      ...getDefaultSalaryRange(employmentType, activeSalary),
      ...this.state.currentSalary,
    };
    const openingInputData = getOpeningSalaryData(salary);

    if (validateFieldsBeforeSave(openingInputData)) {
      this.props.setSalary({
        salaryRange: openingInputData,
      });
    }
  }

  onSalaryChanged = (key, value) => {
    this.setState(({ currentSalary }) => {
      return {
        currentSalary: {
          ...currentSalary,
          [key]: isNil(value) || size(value) === 0 ? null : Number(value),
        },
      };
    }, this.setSalaryBudget);
  };

  selectSalaryRange = ({ from, to }) =>
    this.setState(({ currentSalary }) => {
      const { openingId } = this.props;
      const updatedCurrentSalary = { ...currentSalary, from, to };

      trackSetSalaryRange({ ...updatedCurrentSalary, openingId });

      return { currentSalary: updatedCurrentSalary };
    }, this.setSalaryBudget);

  onCurrencyChange = ({ target: { value: currencyId } }) => {
    const { openingId } = this.props;
    const { currentSalary } = this.state;

    const currency = find(this.state.salaryCurrencies, { id: currencyId });

    this.setState(({ currentSalary }) => ({ currentSalary: { ...currentSalary, currency } }), this.setSalaryBudget);

    trackSetSalaryRange({
      ...currentSalary,
      currency: currency.code,
      openingId: openingId,
    });
  };

  onPeriodChange = period => {
    const { openingId } = this.props;
    const { currentSalary } = this.state;

    this.setState(
      ({ currentSalary }) => ({
        currentSalary: { ...currentSalary, period: period.key },
      }),
      () => {
        this.setState({ activePeriodTab: period });
        this.setSalaryBudget();
      },
    );

    trackSetSalaryRange({
      ...currentSalary,
      openingId: openingId,
      period: period.key,
    });
  };

  onMaxInputBlur = () => {
    this.setState(({ currentSalary }) => ({
      maxInvalid: currentSalary.from > currentSalary.to,
    }));
  };

  render() {
    const { currentSalary, salaryCurrencies, maxInvalid, activePeriodTab } = this.state;

    const { openingId, updatedAt } = this.props;

    return (
      <div className="salary-budget">
        <SalariesChart
          from={currentSalary.from}
          openingId={openingId}
          selectSalaryRange={this.selectSalaryRange}
          to={currentSalary.to}
          updatedAt={updatedAt}
        />

        <div className="salary-budget-targeting">
          <div className="salary-budget-targeting-block">
            <SectionTitle
              size="S"
              subtitle={
                <span>
                  Specify the <strong>gross salary range</strong>
                </span>
              }
              title={'Target group'}
            />

            <Tabs activeTab={activePeriodTab} onSelect={this.onPeriodChange} tabs={PERIOD_TABS} />

            <div className="salary-budget-range salary-budget-targeting-amount">
              <TextField
                defaultValue={currentSalary.from}
                label="Min"
                onChange={value => this.onSalaryChanged('from', value)}
                placeholder="Min"
                type="number"
                wrapperClassName="value-from"
              />
              <span>&mdash;</span>
              <TextField
                defaultValue={currentSalary.to}
                label="Max"
                messageType={maxInvalid && 'error'}
                onBlur={e => {
                  const { currentSalary } = this.state;
                  this.onMaxInputBlur();

                  if (currentSalary.from && e.target.value) {
                    trackSetSalaryRange({
                      ...this.state.currentSalary,
                      openingId: openingId,
                      to: e.target.value,
                    });
                  }
                }}
                onChange={value => this.onSalaryChanged('to', value)}
                placeholder="Max"
                type="number"
                wrapperClassName="value-to"
              />
              <div className="salary-budget-value-currencies">
                <select
                  onChange={this.onCurrencyChange}
                  value={get(currentSalary, 'currency.id') || currentSalary.currency}
                >
                  {salaryCurrencies.map(currency => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <GrossSalaryNotice />
          </div>
        </div>
      </div>
    );
  }
}
